import moment from "moment";

declare const wc_bookings: any;

interface WcBookingsDate {
    date_picker: { value: string };
}

const HALL_RENTAL_PRODUCT_ID = 329;

export const BookingConfig = {
    weekendHourSlots: [9, 12, 15, 18]
};

export const BookingLogic = {
    /**
     * Check if the given date is a weekend (Saturday or Sunday).
     * @param date - The Moment date to check.
     * @returns True if the date is a weekend, false otherwise.
     */
    isWeekend(date: moment.Moment): boolean {
        return date.day() == 6 || date.day() == 0;
    },

    /**
     * Determine if a time slot should be removed based on weekend availability.
     * @param date - The Moment date to check.
     * @returns True if the time slot should be removed, false otherwise.
     */
    shouldRemoveTime(date: moment.Moment): boolean {
        if (!BookingLogic.isWeekend(date)) return false;

        return !BookingConfig.weekendHourSlots.includes(date.hour());
    },

    /**
     * Check if two time intervals overlap.
     * @param start1 - Start of the first interval.
     * @param end1 - End of the first interval.
     * @param start2 - Start of the second interval.
     * @param end2 - End of the second interval.
     * @returns True if the intervals overlap, false otherwise.
     */
    isOverlapping(start1: moment.Moment, end1: moment.Moment, start2: moment.Moment, end2: moment.Moment): boolean {
        return start2.isSameOrBefore(end1) && end2.isSameOrAfter(start1);
    }
}

export const BookingService = {
    /**
     * Fetch agility bookings from the backend within the specified date range.
     * @param start - The start date in YYYYMMDDHHmmss format.
     * @param end - The end date in YYYYMMDDHHmmss format.
     * @returns A promise that resolves to the agility bookings data.
     */
    async getAgilityBookings(start: string, end: string): Promise<object> {
        const res = await fetch(`/?wc-ajax=get_agility_bookings&booking_start=${start}&booking_end=${end}`);
        return await res.json();
    },

    /**
     * Fetch product slots from the backend within the specified date range.
     * @param productId - The ID of the product.
     * @param start - The start date in YYYY-MM-DD format.
     * @param end - The end date in YYYY-MM-DD format.
     * @returns A promise that resolves to the product slots data.
     */
    async getProductSlots(productId: number, start: string, end: string): Promise<object> {
        const res = await fetch(
            `/wp-json/wc-bookings/v1/products/slots?min_date=${start}&max_date=${end}&product_ids=${productId}`
        );

        return await res.json();
    },

    /**
     * Fetch the current shopping cart data.
     * @returns A promise that resolves to the cart data.
     */
    async getCart(): Promise<object> {
        const cartResponse = await fetch("/wp-json/wc/store/cart");
        return await cartResponse.json();
    }
};

class CusomAvailabilitySelector {
    productSlots: any[] = [];

    constructor() {
        wc_bookings.hooks.addAction("wc_bookings_date_selected", "dhp", (selectedDate: WcBookingsDate) => this.showAvailableTimes(selectedDate));
        jQuery(document).on("click", "#available-times li", this.setStartTimeAutomatically);
        jQuery(document).on("click", "#wc-bookings-form-start-time", this.resetAgilityEquipmentField);
        jQuery(document).on("change", "#wc-bookings-form-end-time", (e: JQuery.Event) => this.updateAgilityEquipmentField(e));

        jQuery(".wc-bookings-date-picker").after(`
            <p id="available-times-title" style="display: none; margin: 20px 0;"><b>Tilgjengelige tider:</b></p>
            <ul id="available-times"></ul>
        `);
    }

    resetAgilityEquipmentField() {
        jQuery(".agility_equipment-field input").prop("checked", false);
        jQuery(".agility_equipment-field").hide();
    }

    updateAgilityEquipmentField(e: JQuery.Event) {
        const agilityEquipmentField = jQuery(".agility_equipment-field");
        agilityEquipmentField.hide();

        const startTime: number = moment.utc(String(jQuery("#wc-bookings-form-start-time").val()).split("+")[0]).unix(); // YYYY-MM-DDTHH:mm:ss+CET
        const durationHours: number = parseInt(String(jQuery("#wc-bookings-form-end-time").val()));
        const endTime: number = startTime + durationHours * 3600;

        // Figure out if there is agility equipment available at the start time.
        const slotsInRange = this.productSlots.filter(slot =>
            slot.timestamp >= startTime &&
            slot.timestamp < endTime &&
            slot.agility == true
        );

        if (!slotsInRange.length) {
            jQuery(".agility_equipment-field").show();
        }
    }

    async setStartTimeAutomatically() {
        const startTime = jQuery("#wc-bookings-form-start-time");
        const time = jQuery(this).find("time").text(); 

        startTime.find("option").each(function() {
            if (jQuery(this).text() === time) {
                startTime.val(jQuery(this).val() as string);
            }
        });

        startTime.trigger("change");
        startTime.trigger("click");
    }

    async showAvailableTimes(selectedDate: WcBookingsDate) {
        jQuery("#available-times-title").hide(); jQuery("#available-times").empty();

        const dateSelected = moment(selectedDate.date_picker.value);

        let [productSlots, agilityBookings]: any[] = await Promise.all([
            BookingService.getProductSlots(HALL_RENTAL_PRODUCT_ID, dateSelected.format("YYYY-MM-DD"), dateSelected.clone().add(1, "day").format("YYYY-MM-DD")),
            BookingService.getAgilityBookings(dateSelected.format("YYYYMMDD000000"), dateSelected.clone().add(1, "day").format("YYYYMMDD000000"))
        ]);

        productSlots = productSlots.records.map((slot: any) => {
            slot.timestamp = moment.utc(slot.date).unix();

            return slot;
        });

        for (let i = 0; i < productSlots.length; i++) {
            for (let j = 0; j < agilityBookings.length; j++) {
                if (productSlots[i].timestamp == agilityBookings[j].timestamp_start) {
                    const bookingDurationHours = (agilityBookings[j].timestamp_end - productSlots[i].timestamp) / 3600;

                    for (let k = 0; k < bookingDurationHours; k++) {
                        productSlots[i + k].agility = true;
                    }
                }
            }
        }

        this.productSlots = productSlots;

        const showAvailability = (records: any[]) => {
            jQuery("#available-times-title").show(); jQuery("#available-times").empty();

            records.forEach((record: any) => {
                const slotStart = moment(record.date);

                if (BookingLogic.shouldRemoveTime(slotStart)) return;

                jQuery("#available-times").append(`
                    <li data-available="${record.available}">
                        <time>${slotStart.format("HH:mm")}</time>
                        <em>${record.available} ledig</em>
                        <span data-agility="${record.agility}">${record.agility ? 'Agility: opptatt' : 'Agility: ledig'}</span>
                    </li>
                `);
            });
        };

        showAvailability(productSlots);
    }
}

if (typeof jQuery != "undefined") {
    jQuery(function() {
        new CusomAvailabilitySelector();
    });
}
